import React, { Component } from "react";
import axios from "axios";
import moment from "moment";
import Helmet from "react-helmet";
import Layout from "../../../Layout/layout";
import { apiHost } from "../../../api/apiClient";
import visionTrakLogo from "../../../Assets/visiontrak-logo-colored.png";
import { cacheRunSheetCapability, getRunSheetCapability } from "./runSheetCapability";
import "../../../styles/reports/runSheets.scss";

class RunSheetDispatchSummary extends Component {
  constructor(props) {
    super(props);
    const query = new URLSearchParams(props.location.search);
    this.state = {
      runDate: query.get("runDate") || moment().format("YYYY-MM-DD"),
      customerId: query.get("customerId") || "",
      customers: [],
      drivers: [],
      actualRunDate: "",
      loading: true,
      errorMessage: "",
      integrationNotConfigured: false,
    };
  }

  componentDidMount() {
    this.initialiseCustomerContext();
  }

  getAuthHeaders = () => ({
    Authorization: "Bearer " + localStorage.getItem("token"),
  });

  isSystemAdmin = () => {
    return (localStorage.getItem("role") || "").toString().toLowerCase() === "systemadmin";
  };

  getStoredCustomer = () => {
    try {
      return JSON.parse(localStorage.getItem("customer")) || {};
    } catch (e) {
      return {};
    }
  };

  initialiseCustomerContext = async () => {
    if (!this.isSystemAdmin()) {
      this.loadSummary();
      return;
    }

    try {
      const response = await axios.get(`${apiHost}/api/Customer/GetAllCustomers?AllRecords=true&SortDirection=asc&SortString=name`, {
        headers: this.getAuthHeaders(),
      });
      const customers = response.data || [];
      const storedCustomer = this.getStoredCustomer();
      const storedCustomerId = storedCustomer.customerId || storedCustomer.id || storedCustomer.CustomerId || "";
      const storedCustomerName = storedCustomer.customerName || storedCustomer.name || storedCustomer.Name || "";
      const requestedCustomer = customers.find((customer) => customer.id === this.state.customerId);
      const storedMatch = customers.find((customer) => customer.id === storedCustomerId)
        || customers.find((customer) => (customer.name || "").toLowerCase() === storedCustomerName.toLowerCase());
      const selectedCustomer = requestedCustomer || storedMatch || null;

      this.setState(
        {
          customers,
          customerId: selectedCustomer ? selectedCustomer.id : "",
        },
        this.loadSummary
      );
    } catch (e) {
      this.setState({
        loading: false,
        errorMessage: "Unable to load customers for run sheet selection.",
      });
    }
  };

  hasGpsCoordinates = (stop) => {
    const latitude = Number(stop.latitude);
    const longitude = Number(stop.longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude !== 0 && longitude !== 0;
  };

  buildDrivers = (stops) => {
    const groups = {};
    stops.forEach((stop) => {
      const driver = (stop.driver || "").trim();
      const truckNo = (stop.truckNo || "").trim();
      const vehicleRegistration = (stop.vehicleRegistration || "").trim();
      const key = `${driver}|${truckNo}|${vehicleRegistration}`;
      const dropNumber = Number(stop.dropNumber);
      if (!groups[key]) {
        groups[key] = { driver, truckNo, vehicleRegistration, stopCount: 0, firstDrop: null, lastDrop: null, missingCoordinateCount: 0, creditStopCount: 0 };
      }
      const group = groups[key];
      group.stopCount += 1;
      group.missingCoordinateCount += this.hasGpsCoordinates(stop) ? 0 : 1;
      group.creditStopCount += stop.stopCredit ? 1 : 0;
      if (Number.isFinite(dropNumber)) {
        group.firstDrop = group.firstDrop === null ? dropNumber : Math.min(group.firstDrop, dropNumber);
        group.lastDrop = group.lastDrop === null ? dropNumber : Math.max(group.lastDrop, dropNumber);
      }
    });
    return Object.values(groups).sort((left, right) =>
      (left.truckNo || "").localeCompare(right.truckNo || "", undefined, { numeric: true, sensitivity: "base" })
      || (left.driver || "").localeCompare(right.driver || "")
    );
  };

  loadSummary = async () => {
    if (this.isSystemAdmin() && !this.state.customerId) {
      this.setState({
        drivers: [],
        actualRunDate: "",
        loading: false,
        errorMessage: "",
        integrationNotConfigured: false,
      });
      return;
    }

    this.setState({ loading: true, errorMessage: "", integrationNotConfigured: false });
    try {
      const storedCustomer = this.getStoredCustomer();
      const capabilityCustomerId = this.state.customerId
        || storedCustomer.customerId
        || storedCustomer.id
        || storedCustomer.CustomerId
        || "";
      const integrationEnabled = await getRunSheetCapability(capabilityCustomerId, this.getAuthHeaders());
      if (!integrationEnabled) {
        this.setState({
          drivers: [],
          actualRunDate: "",
          loading: false,
          integrationNotConfigured: true,
          errorMessage: "",
        });
        return;
      }

      const params = new URLSearchParams();
      params.set("runDate", this.state.runDate);
      if (this.state.customerId) params.set("customerId", this.state.customerId);
      const response = await axios.get(`${apiHost}/api/RunSheets/dispatch-summary?${params.toString()}`, { headers: this.getAuthHeaders() });
      this.setState({
        drivers: response.data && response.data.drivers ? response.data.drivers : [],
        actualRunDate: response.data && response.data.runDate ? response.data.runDate : this.state.runDate,
        loading: false,
      });
    } catch (error) {
      const status = error && error.response ? error.response.status : null;
      const code = error && error.response && error.response.data ? error.response.data.code : "";
      const integrationNotConfigured = status === 404 && code === "IntegrationNotConfigured";
      if (integrationNotConfigured) {
        const storedCustomer = this.getStoredCustomer();
        cacheRunSheetCapability(
          this.state.customerId || storedCustomer.customerId || storedCustomer.id || storedCustomer.CustomerId || "",
          false
        );
      }
      this.setState({
        drivers: [],
        loading: false,
        integrationNotConfigured,
        errorMessage: integrationNotConfigured ? "" : "Unable to load the run sheet summary.",
      });
    }
  };

  handleCustomerChange = (event) => {
    this.setState(
      {
        customerId: event.target.value,
        drivers: [],
        actualRunDate: "",
        integrationNotConfigured: false,
      },
      this.loadSummary
    );
  };

  renderIntegrationMessage = () => {
    if (!this.state.integrationNotConfigured) {
      return null;
    }

    return (
      <section className="runsheetIntegrationMessage">
        <div className="runsheetIntegrationCopy">
          <img src={visionTrakLogo} alt="VisionTrak" />
          <span>ERP Integration</span>
          <h3>Connect your ERP run sheets to VisionTrak.</h3>
          <p>
            ERP run sheet integration is not currently enabled for your organisation. VisionTrak can import delivery runs, stops, driver assignments, truck details, and order information from supported ERP systems.
          </p>
          <p>
            To connect your current ERP solution, please contact our team and we will help confirm the available integration options.
          </p>
        </div>
        <div className="runsheetIntegrationGraphic" aria-hidden="true">
          <div className="runsheetSheetWave">
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
            <div><span></span><span></span><span></span></div>
          </div>
          <div className="runsheetSystemNode runsheetSystemNodeErp">
            <strong>ERP</strong>
            <span>Orders</span>
          </div>
          <div className="runsheetDataFlow">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="runsheetSystemNode runsheetSystemNodeVision">
            <strong>VisionTrak</strong>
            <span>Run Sheets</span>
          </div>
          <div className="runsheetMiniSheet">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </section>
    );
  };

  handleDateChange = (event) => {
    this.setState(
      {
        runDate: event.target.value,
        drivers: [],
        actualRunDate: "",
      },
      this.loadSummary
    );
  };

  backToRunSheets = () => {
    const params = new URLSearchParams();
    params.set("runDate", this.state.runDate);
    if (this.state.customerId) params.set("customerId", this.state.customerId);
    this.props.history.push(`/Rpt-RunSheets?${params.toString()}`);
  };

  viewRunSheet = (driver) => {
    const params = new URLSearchParams();
    params.set("runDate", this.state.runDate);
    if (this.state.customerId) params.set("customerId", this.state.customerId);
    if (driver.driver) params.set("driver", driver.driver);
    if (driver.truckNo) params.set("truckNo", driver.truckNo);
    this.props.history.push(`/Rpt-RunSheets?${params.toString()}`);
  };

  render() {
    const totalStops = this.state.drivers.reduce((total, driver) => total + driver.stopCount, 0);
    const needsCustomerSelection = this.isSystemAdmin() && !this.state.customerId;
    return (
      <Layout>
        <Helmet><title>Run Sheet Summary</title></Helmet>
        <div className="runsheetPage runsheetDispatchPage">
          <section className="runsheetHero">
            <div className="runsheetToolbar">
              <div>
                <span className="runsheetDispatchEyebrow">Imported Accredo run sheet</span>
                <h2>Run Sheet Summary</h2>
                <p>{moment(this.state.actualRunDate || this.state.runDate).format("dddd Do MMMM, YYYY")} · {this.state.drivers.length} runs · {totalStops} stops</p>
              </div>
              <div className="runsheetFilters runsheetDispatchFilters">
                {this.isSystemAdmin() && (
                  <select value={this.state.customerId} onChange={this.handleCustomerChange}>
                    <option value="">Select customer</option>
                    {this.state.customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                )}
                <input type="date" value={this.state.runDate} onChange={this.handleDateChange} />
                <button type="button" className="runsheetDispatchRefresh" onClick={this.loadSummary} disabled={this.state.loading || needsCustomerSelection}>
                  {this.state.loading ? "Refreshing..." : "Refresh"}
                </button>
                <button type="button" className="runsheetBackButton" onClick={this.backToRunSheets}>Run Sheets</button>
              </div>
            </div>
          </section>
          {this.state.errorMessage && <div className="runsheetError">{this.state.errorMessage}</div>}
          {needsCustomerSelection && !this.state.loading && !this.state.errorMessage && (
            <div className="runsheetInfo">Select a customer to load run sheet summary data.</div>
          )}
          {this.renderIntegrationMessage()}
          {this.state.loading ? <div className="runsheetLoading">Loading run sheet summary...</div> : !needsCustomerSelection && !this.state.integrationNotConfigured ? (
            <section className="runsheetDispatchList runsheetDispatchPageList">
              {this.state.drivers.map((driver, index) => (
                <article className={driver.isVisiontrakLinked ? "runsheetDispatchCard" : "runsheetDispatchCard unlinked"} key={`${driver.driver}|${driver.truckNo}|${driver.vehicleRegistration}|${index}`}>
                  <div className="runsheetDispatchIdentity">
                    <strong>{driver.driver || "Unassigned driver"}</strong>
                    <span>Truck {driver.truckNo || "—"}{driver.vehicleRegistration ? ` · ${driver.vehicleRegistration}` : ""}</span>
                    {!driver.isVisiontrakLinked && <em className="runsheetUnlinkedBadge">Not linked to a Visiontrak asset</em>}
                  </div>
                  <div className="runsheetDispatchStats">
                    <span><strong>{driver.stopCount}</strong> stops</span>
                    <span><strong>{driver.firstDrop === null ? "—" : driver.firstDrop}–{driver.lastDrop === null ? "—" : driver.lastDrop}</strong> drops</span>
                    <span className={driver.missingCoordinateCount ? "warning" : ""}><strong>{driver.missingCoordinateCount}</strong> missing GPS</span>
                    <span><strong>{driver.creditStopCount}</strong> credits</span>
                  </div>
                  <button type="button" onClick={() => this.viewRunSheet(driver)}>View run sheet</button>
                </article>
              ))}
              {!this.state.drivers.length && <div className="runsheetEmpty">No driver or truck assignments are available.</div>}
            </section>
          ) : null}
        </div>
      </Layout>
    );
  }
}

export default RunSheetDispatchSummary;
